/**
 * Clock — 시간에 관한 모든 것.
 *
 * 이 모듈의 전제는 하나다. **브라우저 RT의 절대값은 믿을 수 없고, 같은 참가자 안에서의
 * 조건 간 차이만 믿는다.** (Bridges et al., PeerJ 2020: RT 지연 8–67 ms, 정밀도 <1–8 ms)
 * 지연(lag)은 대체로 상수라 차이 점수에서 상쇄되지만, 분산(jitter)은 상쇄되지 않고
 * 신호 위에 그대로 얹힌다. 따라서 목표는 "빠르게"가 아니라 "일정하게"다.
 *
 * 그래서 지속시간을 ms 가 아니라 **프레임 수**로 표현한다. 60 Hz 에서 500 ms 는 30 프레임이고,
 * 우리가 실제로 통제할 수 있는 단위도 프레임이다.
 */

/** 실측에서 흔히 나오는 주사율. 측정값을 여기에 스냅해 부동소수 잡음을 제거한다. */
const KNOWN_RATES = [50, 60, 75, 90, 100, 120, 144, 165, 180, 240] as const;
const SNAP_TOLERANCE = 0.02; // ±2 %

export interface RefreshEstimate {
  /** 스냅된 주사율(Hz). 실제 계산에는 이 값을 쓴다. */
  hz: number;
  /** 스냅 전 원시 추정치. 로그용. */
  rawHz: number;
  /** 한 프레임의 길이(ms) = 1000 / hz */
  frameMs: number;
  medianDeltaMs: number;
  /** 프레임 간격의 표준편차. 이 값이 크면 그 기기는 실험에 부적합하다. */
  sdDeltaMs: number;
  sampleCount: number;
  droppedDuringProbe: number;
  /** 표본이 충분하고 분산이 작아 신뢰할 만한가. false 면 세션 진입을 막는 근거가 된다. */
  confident: boolean;
}

export interface FrameFit {
  frames: number;
  /** 프레임 수로 표현했을 때 실제로 제시되는 시간 */
  actualMs: number;
  /** 요청한 시간과의 차이 */
  errorMs: number;
  /** 정수 프레임으로 딱 떨어지는가. false 면 프로토콜과 데이터 양쪽에 실측값을 남겨야 한다. */
  exact: boolean;
}

/* ────────────────────────────────────────────────────────────
   주사율 측정
   ──────────────────────────────────────────────────────────── */

/**
 * rAF 간격을 모아 주사율을 추정한다.
 *
 * 첫 몇 프레임은 탭 활성화·합성기 예열 때문에 튄다 → 버린다.
 * 평균이 아니라 중앙값을 쓴다 → 드롭된 프레임 하나가 추정치를 통째로 망치는 것을 막는다.
 */
export function measureRefreshRate(durationMs = 1000): Promise<RefreshEstimate> {
  return new Promise((resolve) => {
    const deltas: number[] = [];
    const WARMUP = 5;
    let seen = 0;
    let last = 0;
    let hasLast = false;
    let start = 0;
    let hasStart = false;

    // 0 을 "아직 없음"의 표시로 쓰지 않는다. rAF 타임스탬프는 0 일 수 있고,
    // 그러면 첫 간격이 통째로 유실된다.
    const step = (ts: number): void => {
      if (!hasStart) { start = ts; hasStart = true; }
      if (hasLast) {
        seen += 1;
        if (seen > WARMUP) deltas.push(ts - last);
      }
      last = ts;
      hasLast = true;
      if (ts - start < durationMs) requestAnimationFrame(step);
      else resolve(summarise(deltas));
    };
    requestAnimationFrame(step);
  });
}

function summarise(deltas: number[]): RefreshEstimate {
  if (deltas.length === 0) {
    return {
      hz: 60, rawHz: 60, frameMs: 1000 / 60, medianDeltaMs: 1000 / 60,
      sdDeltaMs: 0, sampleCount: 0, droppedDuringProbe: 0, confident: false,
    };
  }
  const sorted = [...deltas].sort((a, b) => a - b);
  const median = quantile(sorted, 0.5);
  const rawHz = 1000 / median;
  const hz = snapToKnownRate(rawHz);
  const frameMs = 1000 / hz;

  // 드롭 판정은 중앙값 기준. 1.5 프레임을 넘긴 간격을 드롭으로 센다.
  const dropped = deltas.reduce((n, d) => n + Math.max(0, Math.round(d / median) - 1), 0);

  // SD 는 드롭을 뺀 정상 프레임에서만 계산한다. 드롭이 섞이면 SD 가 의미를 잃는다.
  const clean = deltas.filter((d) => d < median * 1.5);
  const mean = clean.reduce((a, b) => a + b, 0) / (clean.length || 1);
  const sd = Math.sqrt(clean.reduce((a, b) => a + (b - mean) ** 2, 0) / (clean.length || 1));

  return {
    hz,
    rawHz,
    frameMs,
    medianDeltaMs: median,
    sdDeltaMs: sd,
    sampleCount: deltas.length,
    droppedDuringProbe: dropped,
    confident: deltas.length >= 30 && sd < frameMs * 0.35 && dropped / deltas.length < 0.05,
  };
}

export function snapToKnownRate(rawHz: number): number {
  let best = rawHz;
  let bestErr = Infinity;
  for (const r of KNOWN_RATES) {
    const err = Math.abs(rawHz - r) / r;
    if (err < bestErr) { bestErr = err; best = r; }
  }
  return bestErr <= SNAP_TOLERANCE ? best : Math.round(rawHz);
}

function quantile(sortedAsc: number[], q: number): number {
  if (sortedAsc.length === 0) return 0;
  const pos = (sortedAsc.length - 1) * q;
  const lo = Math.floor(pos);
  const hi = Math.ceil(pos);
  const a = sortedAsc[lo] ?? 0;
  const b = sortedAsc[hi] ?? a;
  return a + (b - a) * (pos - lo);
}

/* ────────────────────────────────────────────────────────────
   프레임 ↔ ms 변환
   ──────────────────────────────────────────────────────────── */

/**
 * 요청한 지속시간을 프레임 수로 바꾼다.
 *
 * 75 Hz 에서 500 ms 는 37.5 프레임이라 정수로 떨어지지 않는다 → 493.3 ms 또는 506.7 ms.
 * 이런 경우를 조용히 반올림하고 넘어가면 프로토콜 명세와 실제 제시 시간이 어긋난 채
 * 데이터가 쌓인다. exact:false 를 반환해 호출부가 반드시 기록하도록 강제한다.
 */
export function fitFrames(requestedMs: number, frameMs: number): FrameFit {
  const raw = requestedMs / frameMs;
  const frames = Math.max(1, Math.round(raw));
  const actualMs = frames * frameMs;
  return {
    frames,
    actualMs,
    errorMs: actualMs - requestedMs,
    exact: Math.abs(raw - frames) < 1e-6,
  };
}

export function framesToMs(frames: number, frameMs: number): number {
  return frames * frameMs;
}

/* ────────────────────────────────────────────────────────────
   실행 중 프레임 품질 감시
   ──────────────────────────────────────────────────────────── */

export interface FrameQuality {
  frames: number;
  droppedFrames: number;
  droppedPct: number;
  meanDeltaMs: number;
  sdDeltaMs: number;
  maxDeltaMs: number;
  /** 세션 도중 주사율이 바뀐 횟수(외부 모니터 연결, 배터리 절약 모드로 120→60 강등 등) */
  refreshChanges: number;
}

/**
 * Runner 의 rAF 루프가 매 프레임 tick(ts) 를 불러준다.
 * 트라이얼 안에서는 객체를 새로 만들지 않는다 — GC 일시정지가 곧 프레임 드롭이다.
 */
export class FrameMonitor {
  #frameMs: number;
  #last = 0;
  #hasLast = false;
  #frames = 0;
  #dropped = 0;
  #sum = 0;
  #sumSq = 0;
  #max = 0;
  #refreshChanges = 0;
  #recentSum = 0;
  #recentN = 0;

  constructor(frameMs: number) { this.#frameMs = frameMs; }

  tick(ts: number): void {
    if (this.#hasLast) {
      const d = ts - this.#last;
      this.#frames += 1;
      this.#dropped += Math.max(0, Math.round(d / this.#frameMs) - 1);
      this.#sum += d;
      this.#sumSq += d * d;
      if (d > this.#max) this.#max = d;

      // 최근 60 프레임의 평균이 현재 프레임 길이에서 20 % 이상 벗어나면 주사율 변경으로 본다.
      this.#recentSum += d;
      this.#recentN += 1;
      if (this.#recentN >= 60) {
        const avg = this.#recentSum / this.#recentN;
        if (Math.abs(avg - this.#frameMs) / this.#frameMs > 0.2) {
          this.#refreshChanges += 1;
          this.#frameMs = avg; // 새 주사율에 맞춰 기준을 옮긴다
        }
        this.#recentSum = 0;
        this.#recentN = 0;
      }
    }
    this.#last = ts;
    this.#hasLast = true;
  }

  /** 트라이얼 경계에서 부른다. 블록 간 휴식 시간이 드롭으로 잡히는 것을 막는다. */
  pause(): void { this.#hasLast = false; }

  get currentFrameMs(): number { return this.#frameMs; }

  snapshot(): FrameQuality {
    const n = this.#frames || 1;
    const mean = this.#sum / n;
    const variance = Math.max(0, this.#sumSq / n - mean * mean);
    return {
      frames: this.#frames,
      droppedFrames: this.#dropped,
      droppedPct: (this.#dropped / n) * 100,
      meanDeltaMs: mean,
      sdDeltaMs: Math.sqrt(variance),
      maxDeltaMs: this.#max,
      refreshChanges: this.#refreshChanges,
    };
  }

  reset(frameMs = this.#frameMs): void {
    this.#frameMs = frameMs;
    this.#last = 0; this.#hasLast = false; this.#frames = 0; this.#dropped = 0;
    this.#sum = 0; this.#sumSq = 0; this.#max = 0;
    this.#refreshChanges = 0; this.#recentSum = 0; this.#recentN = 0;
  }
}

/* ────────────────────────────────────────────────────────────
   타이머 해상도
   ──────────────────────────────────────────────────────────── */

/**
 * performance.now() 의 실제 눈금을 측정한다.
 *
 * Spectre 완화로 브라우저가 타이머를 의도적으로 뭉갠다.
 * 교차출처 격리(COOP+COEP) 가 걸려 있으면 훨씬 고운 눈금이 나온다.
 *   Chrome ~100 µs → 5 µs,  Firefox 1 ms → 20 µs
 * 눈금이 1 ms 면 RT 측정에 1 ms 의 양자화 오차가 그대로 들어온다.
 */
export function measureTimerResolution(samples = 20_000): number {
  let min = Infinity;
  let prev = performance.now();
  for (let i = 0; i < samples; i += 1) {
    const t = performance.now();
    const d = t - prev;
    if (d > 0 && d < min) min = d;
    prev = t;
  }
  return Number.isFinite(min) ? min : 0;
}

/**
 * rAF 콜백의 타임스탬프는 **그 프레임의 vsync 시각**이지, 화면에 빛이 나온 시각이 아니다.
 * 이 콜백 안에서 그린 내용은 통상 다음 vsync 에 표시된다. 그래서 자극 온셋의 최선 추정은
 * "콜백 타임스탬프 + 한 프레임"이다.
 *
 * 이 보정은 상수이므로 차이 점수에서 상쇄된다. 그래도 절대 RT 를 남길 때는 적용해 둔다.
 */
export function estimateOnsetFromFrame(frameTimestamp: number, frameMs: number): number {
  return frameTimestamp + frameMs;
}

export const isCrossOriginIsolated = (): boolean =>
  typeof crossOriginIsolated === 'boolean' ? crossOriginIsolated : false;
